import React from 'react';
import { css } from '@emotion/react';
import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { theme } from 'styles/theme';
import { Tooltip } from './Tooltip';

export const ProcessStep = ({
	isDone,
	isDisabled,
	isIncomplete,
	isUrgent,
	children,
	to,
	hasStateIcon,
	canBeFilledLater,
}: {
	isDone?: boolean;
	isDisabled?: boolean;
	isIncomplete?: boolean;
	isUrgent?: boolean;
	children: ReactNode;
	to: string;
	hasStateIcon: boolean;
	canBeFilledLater?: boolean;
}) => {
	return (
		<>
			{isDisabled ? (
				<div
					css={css`
						margin: 0;
						padding: 0 24px 0 32px;
						font-weight: 500;
						color: ${theme.color.text['300']};
						height: 44px;
						display: flex;
						align-items: center;
						justify-content: space-between;

						${(isDone || !isDisabled) &&
						css`
							color: ${theme.color.text['400']};
						`}

						&.active {
							color: ${theme.color.primary['400']};
							box-shadow: inset 4px 0 0 ${theme.color.primary['400']};
						}

						${isDisabled &&
						css`
							&:hover {
								cursor: not-allowed;
							}
						`}
					`}
				>
					<div>{children}</div>

					{hasStateIcon && (
						<div>
							<Tooltip
								content={
									isDone ? (
										'👍 Vše potřebné je vyplněno.'
									) : isUrgent ? (
										<>
											<strong>Urgentně doplňte údaje.</strong>
											<br /> Jinak může dojít ke zrušení vystoupení.
											<br />
											<span
												css={css`
													font-style: italic;
												`}
											>
												Je potřeba vyplnit předchozí krok.
											</span>
										</>
									) : (
										<>
											{canBeFilledLater ? (
												<>
													Tyto údaje je možné doplnit <br />
													<strong>nejpozději 14 dnů před vystoupením.</strong>
												</>
											) : (
												<>
													<strong>Povinné údaje</strong> pro dokončení bookingu.
												</>
											)}
										</>
									)
								}
							>
								<IconState
									isDone={isDone}
									isIncomplete={isIncomplete}
									isIncompleteDanger={isUrgent}
									isDisabled={true}
								/>
							</Tooltip>
						</div>
					)}
				</div>
			) : (
				<NavLink
					to={to}
					css={css`
						margin: 0;
						padding: 0 24px 0 32px;
						font-weight: 500;
						color: ${theme.color.text['300']};
						height: 44px;
						display: flex;
						align-items: center;
						justify-content: space-between;

						&:hover {
							text-decoration: none;
						}

						${(isDone || !isDisabled) &&
						css`
							color: ${theme.color.text['400']};
						`}

						&.active {
							color: ${theme.color.primary['400']};
							box-shadow: inset 4px 0 0 ${theme.color.primary['400']};
						}

						${isDisabled &&
						css`
							&:hover {
								cursor: not-allowed;
							}
						`}
					`}
				>
					<div>{children}</div>

					{hasStateIcon && (
						<div>
							<Tooltip
								content={
									isDone ? (
										'👍 Vše potřebné je vyplněno.'
									) : isUrgent ? (
										<>
											<strong>Urgentně doplňte údaje.</strong>
											<br /> Jinak může dojít ke zrušení vystoupení.
										</>
									) : (
										<>
											{canBeFilledLater ? (
												<>
													Tyto údaje je možné doplnit <br />
													<strong>nejpozději 14 dnů před vystoupením.</strong>
												</>
											) : (
												<>
													<strong>Povinné údaje</strong> pro dokončení bookingu.
												</>
											)}
										</>
									)
								}
							>
								<IconState
									isDone={isDone}
									isIncomplete={isIncomplete}
									isIncompleteDanger={isUrgent && !isDone}
								/>
							</Tooltip>
						</div>
					)}
				</NavLink>
			)}
		</>
	);
};

export const IconState = ({
	isDone,
	isIncomplete,
	isIncompleteDanger,
	isDisabled,
	sizeSmall,

	isSquared,
}: {
	isDone?: boolean;
	isIncomplete?: boolean;
	isIncompleteDanger?: boolean;
	isDisabled?: boolean;
	sizeSmall?: boolean;
	isSquared?: boolean;
}) => {
	return (
		<div
			css={css`
				width: ${sizeSmall ? 16 : 20}px;
				height: ${sizeSmall ? 16 : 20}px;
				background: red;
				border-radius: 20px;
				background: #d4d5de;
				color: #fff;
				display: flex;
				align-items: center;
				justify-content: center;

				${isSquared &&
				css`
					width: 100%;
					height: 16px;
				`}

				${isDone &&
				css`
					background: #46cb73;
				`};

				${isIncomplete &&
				css`
					background: #ffca6a;
				`};

				${isIncompleteDanger &&
				css`
					background: #ff6767;

					${isDisabled &&
					css`
						background: #ffb3b3;
					`}
				`};

				${sizeSmall &&
				css`
					svg {
						width: 10px;
						height: 10px;
					}
				`}
			`}
		>
			{isDone ? <IconDone /> : <IconQuestion />}
		</div>
	);
};

export const IconDone = () => {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
			<polygon
				fill="currentColor"
				points="8.289 -.703 9.711 .703 3.074 7.422 -.711 3.597 .711 2.191 3.072 4.577"
				transform="translate(1.5 3)"
			/>
		</svg>
	);
};

export const IconQuestion = () => {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
			<path
				fill="currentColor"
				d="M1.84943182,6.17471591 L1.84943182,6 C1.84943182,5.51704545 1.88991477,5.13210227 1.97088068,4.84517045 C2.05184659,4.55823864 2.17045455,4.32741477 2.32670455,4.15269886 C2.48295455,3.97798295 2.67329545,3.81818182 2.89772727,3.67329545 C3.07670455,3.55965909 3.23650568,3.44389205 3.37713068,3.32599432 C3.51775568,3.20809659 3.62926136,3.08096591 3.71164773,2.94460227 C3.79403409,2.80823864 3.83522727,2.65340909 3.83522727,2.48011364 C3.83522727,2.3125 3.79545455,2.16477273 3.71590909,2.03693182 C3.63636364,1.90909091 3.52769886,1.81036932 3.38991477,1.74076705 C3.25213068,1.67116477 3.09943182,1.63636364 2.93181818,1.63636364 C2.76420455,1.63636364 2.60724432,1.67471591 2.4609375,1.75142045 C2.31463068,1.828125 2.1953125,1.93678977 2.10298295,2.07741477 C2.01065341,2.21803977 1.96306818,2.38778409 1.96022727,2.58664773 L0,2.58664773 C0.00852272727,1.99005682 0.147017045,1.50142045 0.415482955,1.12073864 C0.683948864,0.740056818 1.03977273,0.458096591 1.48295455,0.274857955 C1.92613636,0.0916193182 2.41477273,0 2.94886364,0 C3.53409091,0 4.0546875,0.0909090909 4.51065341,0.272727273 C4.96661932,0.454545455 5.32528409,0.723011364 5.58664773,1.078125 C5.84801136,1.43323864 5.97869318,1.86789773 5.97869318,2.38210227 C5.97869318,2.72017045 5.921875,3.01846591 5.80823864,3.27698864 C5.69460227,3.53551136 5.53622159,3.76349432 5.33309659,3.9609375 C5.12997159,4.15838068 4.89204545,4.33806818 4.61931818,4.5 C4.40625,4.625 4.22940341,4.75568182 4.08877841,4.89204545 C3.94815341,5.02840909 3.84232955,5.18394886 3.77130682,5.35866477 C3.70028409,5.53338068 3.66477273,5.74715909 3.66477273,6 L3.66477273,6.17471591 L1.84943182,6.17471591 Z M2.79119318,8.97017045 C2.484375,8.97017045 2.22230114,8.86221591 2.00497159,8.64630682 C1.78764205,8.43039773 1.68039773,8.16903409 1.68323864,7.86221591 C1.68039773,7.56107955 1.78764205,7.30326705 2.00497159,7.08877841 C2.22230114,6.87428977 2.484375,6.76704545 2.79119318,6.76704545 C3.08380682,6.76704545 3.34090909,6.87428977 3.5625,7.08877841 C3.78409091,7.30326705 3.89630682,7.56107955 3.89914773,7.86221591 C3.89630682,8.06676136 3.84303977,8.25284091 3.73934659,8.42045455 C3.63565341,8.58806818 3.50071023,8.72159091 3.33451705,8.82102273 C3.16832386,8.92045455 2.98721591,8.97017045 2.79119318,8.97017045 Z"
				transform="translate(2.966 1.153)"
			/>
		</svg>
	);
};
